import React, { useEffect, useState } from 'react';
import { Bot } from 'lucide-react';
import { CLIENT_TYPE_OPTIONS, MCP_PUBLIC_URL, SINGLETON_ENDPOINT_ID, TRUST_POLICY_OPTIONS, clientTypeLabel } from '../governedMcpConstants';
import { createId } from '../governedMcpStore';
import type { DirectoryGroup, DirectoryUser, McpClient } from '../governedMcpTypes';
import { CheckRow, FormSection, FormStack, GatewayDrawer, fieldClass, ghostBtn, labelClass, panelClass, primaryBtn } from '../components/GatewayUi';

function blankClient(): McpClient {
  return {
    id: createId('cl'),
    name: '',
    clientType: 'claude_enterprise',
    registrationId: createId('mcp-client'),
    authConfig: 'OIDC; Selfic maps IdP subject to enterprise user. Client-supplied identity is ignored.',
    endpointId: SINGLETON_ENDPOINT_ID,
    environment: 'development',
    trustPolicy: 'enterprise_managed',
    allowedUserIds: [],
    allowedGroupIds: [],
    status: 'active',
    createdBy: 'Current administrator',
    lastConnectedAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export default function ClientFormDrawer({
  open,
  initial,
  users,
  groups,
  isLight,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: McpClient | null;
  users: DirectoryUser[];
  groups: DirectoryGroup[];
  isLight: boolean;
  onClose: () => void;
  onSave: (client: McpClient) => void;
}) {
  const [form, setForm] = useState<McpClient>(blankClient());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...initial, endpointId: SINGLETON_ENDPOINT_ID } : blankClient());
      setError(null);
    }
  }, [open, initial]);

  const toggleId = (list: string[], id: string) => (list.includes(id) ? list.filter((item) => item !== id) : [...list, id]);

  const submit = () => {
    if (!form.name.trim()) {
      setError('Client name is required.');
      return;
    }
    onSave({ ...form, endpointId: SINGLETON_ENDPOINT_ID, environment: 'production' });
  };

  const namedTrust = form.trustPolicy === 'named_users_groups' || form.trustPolicy === 'custom';

  return (
    <GatewayDrawer
      open={open}
      eyebrow="Governed MCP / Clients"
      icon={<Bot size={18} className="text-indigo-500" />}
      title={initial ? form.name || 'Edit MCP client' : 'Register AI client'}
      subtitle="The client requests. Selfic maps identity and decides what it may discover or invoke."
      status={{ label: form.status === 'active' ? 'Active' : form.status, tone: form.status === 'active' ? 'success' : 'neutral' }}
      isLight={isLight}
      size="xl"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryBtn()} onClick={submit}>{initial ? 'Save client' : 'Register client'}</button>
        </>
      }
    >
      {error ? <p className="mb-4 text-sm text-red-500">{error}</p> : null}
      <FormStack>
        <div className="space-y-5">
          <FormSection title="Identity" description="How this AI application is registered in Selfic." isLight={isLight}>
            <div className="space-y-4">
              <div>
                <label className={labelClass(isLight)}>Client name</label>
                <input className={fieldClass(isLight)} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Claude Enterprise — Risk Desk" />
              </div>
              <div>
                <label className={labelClass(isLight)}>Client type</label>
                <select className={fieldClass(isLight)} value={form.clientType} onChange={(e) => setForm((p) => ({ ...p, clientType: e.target.value as McpClient['clientType'] }))}>
                  {CLIENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                  {CLIENT_TYPE_OPTIONS.find((option) => option.value === form.clientType)?.hint}
                </p>
              </div>
              <div>
                <label className={labelClass(isLight)}>Registration identifier</label>
                <input className={fieldClass(isLight)} value={form.registrationId} onChange={(e) => setForm((p) => ({ ...p, registrationId: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass(isLight)}>Authentication configuration</label>
                <textarea className={fieldClass(isLight)} rows={2} value={form.authConfig} onChange={(e) => setForm((p) => ({ ...p, authConfig: e.target.value }))} />
              </div>
            </div>
          </FormSection>

          <FormSection title="Trust policy" description="Who this client may act on behalf of." isLight={isLight}>
            <div>
              <label className={labelClass(isLight)}>Policy</label>
              <select className={fieldClass(isLight)} value={form.trustPolicy} onChange={(e) => setForm((p) => ({ ...p, trustPolicy: e.target.value as McpClient['trustPolicy'] }))}>
                {TRUST_POLICY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                {TRUST_POLICY_OPTIONS.find((option) => option.value === form.trustPolicy)?.hint}
              </p>
            </div>
            {namedTrust ? (
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <p className={labelClass(isLight)}>Allowed users</p>
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {users.map((user) => (
                      <CheckRow
                        key={user.id}
                        label={user.name}
                        checked={form.allowedUserIds.includes(user.id)}
                        onChange={() => setForm((p) => ({ ...p, allowedUserIds: toggleId(p.allowedUserIds, user.id) }))}
                        isLight={isLight}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <p className={labelClass(isLight)}>Allowed groups</p>
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {groups.map((group) => (
                      <CheckRow
                        key={group.id}
                        label={group.name}
                        checked={form.allowedGroupIds.includes(group.id)}
                        onChange={() => setForm((p) => ({ ...p, allowedGroupIds: toggleId(p.allowedGroupIds, group.id) }))}
                        isLight={isLight}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </FormSection>
        </div>

        <div className="space-y-4">
          <section className={`${panelClass(isLight)} p-5`}>
            <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Registration</h3>
            <dl className={`mt-3 space-y-3 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              <div>
                <dt>Type</dt>
                <dd className={isLight ? 'text-neutral-900' : 'text-white'}>{clientTypeLabel(form.clientType)}</dd>
              </div>
              <div>
                <dt>MCP URL</dt>
                <dd className={`font-mono break-all ${isLight ? 'text-neutral-900' : 'text-white'}`}>{MCP_PUBLIC_URL}</dd>
              </div>
              <div>
                <dt>Trust</dt>
                <dd className={`capitalize ${isLight ? 'text-neutral-900' : 'text-white'}`}>{form.trustPolicy.replace(/_/g, ' ')}</dd>
              </div>
            </dl>
          </section>
          <section className={`${panelClass(isLight)} p-5`}>
            <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Identity rule</h3>
            <p className={`text-sm mt-2 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              Client-asserted user ids are ignored. The IdP subject is mapped to the enterprise user, then existing Selfic permissions apply.
            </p>
          </section>
        </div>
      </FormStack>
    </GatewayDrawer>
  );
}
